(function announcementModule(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  root.EbAnnouncementTime = api;

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const {
      zonedTimeToEpoch,
      remainingParts,
      computeEtaRange,
      formatEtaText,
      parseLeadTimes,
    } = api;

    class EbAnnouncementBar extends HTMLElement {
      connectedCallback() {
        this.storageKey = `eb-annbar-dismissed-${this.dataset.sectionId}`;
        if (this.dataset.dismissible === 'true' && sessionStorage.getItem(this.storageKey)) {
          this.hidden = true;
          return;
        }

        this.track = this.querySelector('[data-eb-annbar-track]');
        this.intervalMs = Number(this.dataset.rotationInterval || 6000);
        this.index = 0;
        this.paused = false;
        this.touchStartX = null;

        this.querySelector('[data-eb-annbar-dismiss]')?.addEventListener('click', () => {
          sessionStorage.setItem(this.storageKey, '1');
          this.hidden = true;
          this.stop();
        });

        this.addEventListener('mouseenter', () => this.pause());
        this.addEventListener('mouseleave', () => this.resume());
        this.addEventListener('focusin', () => this.pause());
        this.addEventListener('focusout', () => this.resume());
        this.addEventListener('touchstart', (event) => {
          this.touchStartX = event.changedTouches[0]?.clientX;
        }, { passive: true });
        this.addEventListener('touchend', (event) => {
          if (this.touchStartX === null) return;
          const delta = event.changedTouches[0]?.clientX - this.touchStartX;
          this.touchStartX = null;
          if (Math.abs(delta) > 40) {
            this.show(delta < 0 ? this.index + 1 : this.index - 1);
          }
        }, { passive: true });
        this.addEventListener('eb:announcement:update', () => this.refreshSlides());

        this.refreshSlides();
      }

      refreshSlides() {
        this.slides = [...this.querySelectorAll('[data-eb-annbar-slide]')].filter((slide) => !slide.hidden || !slide.dataset.dynamicHidden);
        this.slides = [...this.querySelectorAll('[data-eb-annbar-slide]')].filter((slide) => slide.dataset.dynamicHidden !== 'true');
        if (!this.slides.length) {
          this.hidden = true;
          return;
        }

        this.hidden = false;
        this.index = Math.min(this.index, this.slides.length - 1);
        this.slides.forEach((slide, index) => {
          slide.hidden = index !== this.index;
          slide.classList.toggle('is-active', index === this.index);
          slide.classList.remove('is-exiting');
        });

        if (this.slides.length > 1) {
          this.start();
        } else {
          this.stop();
        }
      }

      start() {
        this.stop();
        this.timer = window.setInterval(() => {
          if (!this.paused) this.show(this.index + 1);
        }, this.intervalMs);
      }

      stop() {
        if (this.timer) window.clearInterval(this.timer);
      }

      pause() {
        this.paused = true;
      }

      resume() {
        this.paused = false;
      }

      show(nextIndex) {
        if (!this.slides?.length) return;
        const previous = this.slides[this.index];
        this.index = (nextIndex + this.slides.length) % this.slides.length;
        const next = this.slides[this.index];
        if (previous === next) return;

        previous.classList.add('is-exiting');
        previous.classList.remove('is-active');
        next.hidden = false;
        next.classList.add('is-active');
        window.setTimeout(() => {
          previous.hidden = true;
          previous.classList.remove('is-exiting');
        }, 370);
      }
    }

    class EbCountdown extends HTMLElement {
      connectedCallback() {
        this.values = {
          days: this.querySelector('[data-eb-countdown-value="days"]'),
          hours: this.querySelector('[data-eb-countdown-value="hours"]'),
          minutes: this.querySelector('[data-eb-countdown-value="minutes"]'),
          seconds: this.querySelector('[data-eb-countdown-value="seconds"]'),
        };
        this.localEnd = this.querySelector('[data-eb-countdown-local]');
        this.expired = this.querySelector('[data-eb-countdown-expired]');
        this.timerNode = this.querySelector('[data-eb-countdown-timer]');
        this.deadline = this.computeDeadline();

        if (!Number.isFinite(this.deadline)) return;

        if (this.localEnd && this.dataset.showLocalEndtime === 'true') {
          const formatted = new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
          }).format(new Date(this.deadline));
          this.localEnd.textContent = this.dataset.localEndTemplate.replace('__TIME__', formatted);
        }

        this.tick();
      }

      disconnectedCallback() {
        if (this.timeout) window.clearTimeout(this.timeout);
      }

      computeDeadline() {
        if (!this.dataset.endDate || !this.dataset.endTime) return Number.NaN;

        if (this.dataset.perMarketEnd === 'true') {
          const [year, month, day] = this.dataset.endDate.split('-').map(Number);
          const [hour, minute] = this.dataset.endTime.split(':').map(Number);
          return new Date(year, month - 1, day, hour, minute || 0, 0, 0).getTime();
        }

        return zonedTimeToEpoch(this.dataset.endDate, this.dataset.endTime, this.dataset.timezone || 'UTC');
      }

      tick() {
        const remaining = this.deadline - Date.now();
        if (remaining <= 0) {
          this.handleExpired();
          return;
        }

        const parts = remainingParts(this.deadline, Date.now());
        this.classList.toggle('is-urgent', remaining < 60 * 60 * 1000);
        this.renderPart('days', parts.days);
        this.renderPart('hours', parts.hours);
        this.renderPart('minutes', parts.minutes);
        this.renderPart('seconds', parts.seconds);

        const delay = 1000 - (Date.now() % 1000);
        this.timeout = window.setTimeout(() => this.tick(), delay);
      }

      renderPart(name, value) {
        const node = this.values[name];
        if (!node) return;
        const text = String(value).padStart(2, '0');
        if (node.textContent !== text) {
          node.textContent = text;
          node.classList.remove('eb-annbar__digit-changed');
          void node.offsetWidth;
          node.classList.add('eb-annbar__digit-changed');
        }
      }

      handleExpired() {
        if (this.dataset.expiredBehavior === 'fallback') {
          if (this.timerNode) this.timerNode.hidden = true;
          if (this.expired) this.expired.hidden = false;
          return;
        }

        const slide = this.closest('[data-eb-annbar-slide]');
        if (slide) {
          slide.dataset.dynamicHidden = 'true';
          slide.hidden = true;
          this.dispatchEvent(new CustomEvent('eb:announcement:update', { bubbles: true }));
        }
      }
    }

    class EbDeliveryEta extends HTMLElement {
      connectedCallback() {
        const leadTimes = parseLeadTimes(this.dataset.leadTimes || '');
        const range = computeEtaRange({
          nowMs: Date.now(),
          countryCode: this.dataset.countryCode,
          countryName: this.dataset.countryName,
          leadTimes,
          cutoffHour: Number(this.dataset.cutoffHour || 0),
          skipWeekends: this.dataset.skipWeekends === 'true',
          storeTimeZone: this.dataset.timezone || 'UTC',
          locale: this.dataset.locale || undefined,
        });

        if (!range) {
          const slide = this.closest('[data-eb-annbar-slide]');
          if (slide) {
            slide.dataset.dynamicHidden = 'true';
            slide.hidden = true;
            this.dispatchEvent(new CustomEvent('eb:announcement:update', { bubbles: true }));
          }
          return;
        }

        const output = this.querySelector('[data-eb-eta-output]');
        if (output) output.innerHTML = formatEtaText(this.dataset.template, range);
      }
    }

    if (!customElements.get('eb-announcement-bar')) customElements.define('eb-announcement-bar', EbAnnouncementBar);
    if (!customElements.get('eb-countdown')) customElements.define('eb-countdown', EbCountdown);
    if (!customElements.get('eb-delivery-eta')) customElements.define('eb-delivery-eta', EbDeliveryEta);
  }
})(globalThis, function factory() {
  function parseDate(dateText) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateText || '').trim());
    if (!match) return null;
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
  }

  function parseTime(timeText) {
    const match = /^(\d{2}):(\d{2})$/.exec(String(timeText || '').trim());
    if (!match) return null;
    return { hour: Number(match[1]), minute: Number(match[2]) };
  }

  function zonedParts(ms, timeZone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const values = {};
    formatter.formatToParts(new Date(ms)).forEach((part) => {
      if (part.type !== 'literal') values[part.type] = Number(part.value);
    });
    return {
      year: values.year,
      month: values.month,
      day: values.day,
      hour: values.hour,
      minute: values.minute,
      second: values.second,
    };
  }

  function offsetForZone(ms, timeZone) {
    const parts = zonedParts(ms, timeZone);
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second || 0);
    return asUtc - ms;
  }

  function zonedTimeToEpoch(dateText, timeText, timeZone) {
    const date = parseDate(dateText);
    const time = parseTime(timeText);
    if (!date || !time || !timeZone) return Number.NaN;

    let utc = Date.UTC(date.year, date.month - 1, date.day, time.hour, time.minute, 0, 0);
    for (let i = 0; i < 4; i += 1) {
      utc = Date.UTC(date.year, date.month - 1, date.day, time.hour, time.minute, 0, 0) - offsetForZone(utc, timeZone);
    }
    return utc;
  }

  function remainingParts(deadlineMs, nowMs) {
    const totalSeconds = Math.max(0, Math.floor((deadlineMs - nowMs) / 1000));
    return {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };
  }

  function parseLeadTimes(text) {
    const map = {};
    String(text || '').split(/\r?\n/).forEach((line) => {
      const trimmed = line.trim();
      const match = /^([A-Za-z]{2}|\*)\s*:\s*(\d+)\s*-\s*(\d+)$/.exec(trimmed);
      if (!match) return;
      map[match[1].toUpperCase()] = {
        min: Number(match[2]),
        max: Number(match[3]),
      };
    });
    return map;
  }

  function addCalendarDays(parts, days) {
    const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days, 12, 0, 0));
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    };
  }

  function dayOfWeek(parts) {
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0)).getUTCDay();
  }

  function addBusinessDays(parts, days, skipWeekends) {
    let cursor = { ...parts };
    let remaining = days;
    while (remaining > 0) {
      cursor = addCalendarDays(cursor, 1);
      const dow = dayOfWeek(cursor);
      if (!skipWeekends || (dow !== 0 && dow !== 6)) remaining -= 1;
    }
    return cursor;
  }

  function localStoreStartDate(nowMs, storeTimeZone, cutoffHour) {
    let parts = zonedParts(nowMs, storeTimeZone);
    let start = { year: parts.year, month: parts.month, day: parts.day };
    if (parts.hour >= cutoffHour) start = addCalendarDays(start, 1);
    return start;
  }

  function formatDateRange(startParts, endParts, locale) {
    const formatter = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });
    const start = new Date(Date.UTC(startParts.year, startParts.month - 1, startParts.day, 12, 0, 0));
    const end = new Date(Date.UTC(endParts.year, endParts.month - 1, endParts.day, 12, 0, 0));
    if (startParts.month === endParts.month) {
      const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(start);
      return `${month} ${startParts.day}-${endParts.day}`;
    }
    return `${formatter.format(start)}-${formatter.format(end)}`;
  }

  function computeEtaRange(options) {
    const leadTimes = options.leadTimes || {};
    const countryCode = String(options.countryCode || '').toUpperCase();
    const lead = leadTimes[countryCode] || leadTimes['*'];
    if (!lead) return null;

    const start = localStoreStartDate(options.nowMs, options.storeTimeZone || 'UTC', Number(options.cutoffHour || 0));
    const minDate = addBusinessDays(start, lead.min, Boolean(options.skipWeekends));
    const maxDate = addBusinessDays(start, lead.max, Boolean(options.skipWeekends));
    return {
      countryCode,
      countryName: options.countryName || countryCode,
      start,
      minDate,
      maxDate,
      dateRange: formatDateRange(minDate, maxDate, options.locale),
    };
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatEtaText(template, eta) {
    return String(template || '')
      .replace('{country}', `<span data-eb-eta-country>${escapeHtml(eta.countryName)}</span>`)
      .replace('{date_range}', `<span data-eb-eta-range>${escapeHtml(eta.dateRange)}</span>`);
  }

  return {
    addBusinessDays,
    computeEtaRange,
    formatEtaText,
    localStoreStartDate,
    parseLeadTimes,
    remainingParts,
    zonedParts,
    zonedTimeToEpoch,
  };
});
